import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./single-text-value-editor.module.scss";
import { useI18N } from "@src/language/language-context";
import { Widget } from "@src/types/dashboards";
import { MachineNameAndSourceId } from "@common/types/server-list-types";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { unitOptionLabelOf } from "@src/cloud-connect/units/utils/unit-option-label-of";
import type { UnitDefinition } from "@src/types/units";
import {
  selectUnitsById,
  selectUnitsStatus,
  unitsFailed,
  unitsLoaded,
  unitsRequested,
} from "@src/reducers/units-slice";
import { getAllSourcesListUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-all-sources-list-use-case";
import { getActiveTimeseriesUseCase } from "@src/cloud-connect/timeseries/use-cases/get-active-timeseries-use-case";
import { getUnitsUseCase } from "@src/cloud-connect/timeseries/use-cases/get-units-use-case";
import {
  buildWidgetQuery,
  getWidgetDataSource,
} from "@src/components/organisms/widgets/widget-query";
import {
  DEFAULT_POLLING_INTERVAL,
  DURATION_GAUGE_WIDGET_MS,
  durationToIso,
  isoToDurationMs,
} from "@src/components/organisms/widgets/constants";
import {
  getNumericNodeUnit,
  toNumericAndStringNodeOptions,
} from "@src/components/organisms/widgets/node-options";
import {
  buildScaleTransforms,
  scaleForField,
  scaleTransformsKey,
} from "@src/components/organisms/widgets/utils/value-scaling";
import { parseUserNumber } from "@src/components/organisms/widgets/utils/parse-user-number";
import { MachineNodeUnitFields } from "@src/components/molecules/machine-node-unit-fields/machine-node-unit-fields";
import { LookbackAndPollingFields } from "@src/components/molecules/lookback-and-polling-fields/lookback-and-polling-fields";
import { ScaleInput } from "@src/components/atoms/scale-input/scale-input";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { CheckBoxWithText } from "@src/components/atoms/check-box-with-text/check-box-with-text";
import {
  ColorSwatchPicker,
  DEFAULT_LABEL_COLOR,
  DEFAULT_TREND_LINE_COLOR,
  TREND_LINE_PALETTE,
} from "@src/components/atoms/color-swatch-picker/color-swatch-picker";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import Spinner from "@src/components/atoms/spinner/spinner";
import { useSaveWidget } from "../hooks/use-save-widget";

const cssClassName = "single-text-value-editor";

export interface SingleTextValueEditorProps {
  widget: Widget;
  isPendingWidget: boolean;
  onClose: () => void;
}

/**
 * Editor for a `single-text-value` widget: one node's current value under a
 * label, with an optional unit and per-widget colours. Owns its own state,
 * its own fetching and its own save.
 */
export const SingleTextValueEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: SingleTextValueEditorProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();
  const units = useSelector(selectUnitsById);
  const unitsStatus = useSelector(selectUnitsStatus);
  const isLoadingUnits = unitsStatus === "idle" || unitsStatus === "loading";

  const savedDataSource = useMemo(
    () => getWidgetDataSource(widget.query),
    [widget.query],
  );
  const savedLabel = widget.presentation?.label ?? "";
  const savedLabelColor = widget.presentation?.label_color ?? null;

  // Label: a saved non-empty one opens on manual, an empty one opens on
  // auto-fill so a freshly templated widget follows the node name until
  // the user's first keystroke. A pending widget always opens on auto-fill:
  // the label it carries comes from the template, not from the user.
  const [label, setLabel] = useState(savedLabel);
  const [shouldAutoFillLabel, setShouldAutoFillLabel] = useState(
    isPendingWidget || savedLabel.trim() === "",
  );
  // Value+unit colour. Null (nothing stored) keeps the widget on the
  // dashboard-theme custom properties.
  const [color, setColor] = useState<string | null>(
    widget.presentation?.color ?? null,
  );
  // Label+timestamp colour. `#ffffff` was written by templates that
  // predate the palette picker; normalise it to the palette's white so
  // the picker shows an active swatch instead of nothing.
  const [labelColor, setLabelColor] = useState<string | null>(
    savedLabelColor != null && savedLabelColor.toLowerCase() === "#ffffff"
      ? DEFAULT_LABEL_COLOR
      : savedLabelColor,
  );
  // Checkbox mirrors stored data on a saved widget: either colour present
  // → open checked. A brand-new (`isPendingWidget`) widget has no real
  // user choice yet — its `presentation` only carries template defaults —
  // so the checkbox opens unchecked; opting in flips state as usual.
  const [isUsingCustomColors, setIsUsingCustomColors] = useState(
    !isPendingWidget &&
      ((widget.presentation?.color ?? null) != null || savedLabelColor != null),
  );
  // Lookback persists in `query.window.duration`; no saved window (or a
  // brand-new widget) falls back to the gauge's 30 min default.
  const [lookbackMs, setLookbackMs] = useState<number | null>(
    isoToDurationMs(widget.query?.window?.duration) ?? DURATION_GAUGE_WIDGET_MS,
  );
  const [pollingInterval, setPollingInterval] = useState<string | null>(
    widget.query?.sampling?.polling_interval ?? DEFAULT_POLLING_INTERVAL,
  );
  // The prefill hands the raw ids forward; the effects below swap in the
  // name each option carries once the lists arrive.
  const [machine, setMachine] = useState<MachineNameAndSourceId | null>(
    savedDataSource
      ? ({
          name: savedDataSource.group,
          source_id: savedDataSource.group,
        } as MachineNameAndSourceId)
      : null,
  );
  const [node, setNode] = useState<MachineNameAndSourceId | null>(
    savedDataSource
      ? ({
          name: savedDataSource.source,
          source_id: savedDataSource.source,
        } as MachineNameAndSourceId)
      : null,
  );
  // The saved node is restored once, when the editor opens on its stored
  // machine. Once the user has picked a machine by hand, recalling the same
  // one must not bring the old node back.
  const machineTouchedRef = useRef(false);
  const [machineOptions, setMachineOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [nodeOptions, setNodeOptions] = useState<MachineNameAndSourceId[]>([]);
  const [nodeUnits, setNodeUnits] = useState<Record<string, number | null>>({});
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const savedUnitId = isPendingWidget
    ? null
    : widget.presentation?.unit ?? null;
  const [unit, setUnit] = useState<UnitDefinition | null>(() =>
    getUnitById(units, savedUnitId),
  );
  const [shouldAutoPickUnit, setShouldAutoPickUnit] = useState(
    savedUnitId == null,
  );
  const [valueScale, setValueScale] = useState(
    String(scaleForField("value", widget.encoding?.transforms)),
  );

  const texts = {
    saveButton: i18n("modules.singleTextValue.saveButton"),
    cancelButton: i18n("modules.singleTextValue.cancelButton"),
    machineLabel: i18n("modules.singleTextValue.machineLabel"),
    noMachines: i18n("modules.singleTextValue.noMachines"),
    machinesPlaceholder: i18n("modules.singleTextValue.machinesPlaceholder"),
    nodeLabel: i18n("modules.singleTextValue.nodeLabel"),
    noNodes: i18n("modules.singleTextValue.noNodes"),
    nodesPlaceholder: i18n("modules.singleTextValue.nodesPlaceholder"),
    unitLabel: i18n("modules.singleTextValue.unitLabel"),
    noUnits: i18n("modules.singleTextValue.noUnits"),
    unitsPlaceholder: i18n("modules.singleTextValue.unitsPlaceholder"),
    labelLabel: i18n("modules.singleTextValue.labelLabel"),
    labelPlaceholder: i18n("modules.singleTextValue.labelPlaceholder"),
    labelRequired: i18n("modules.singleTextValue.labelRequired"),
    labelColorLabel: i18n("modules.singleTextValue.labelColorLabel"),
    valueColorLabel: i18n("modules.singleTextValue.valueColorLabel"),
    customColorsCheckbox: i18n("modules.singleTextValue.customColorsCheckbox"),
    machineRequired: i18n("modules.singleTextValue.machineRequired"),
    nodeRequired: i18n("modules.singleTextValue.nodeRequired"),
    lookbackRequired: i18n("modules.singleTextValue.lookbackRequired"),
    scaleInvalid: i18n("modules.singleTextValue.scaleInvalid"),
  };

  const unitOptions = Object.entries(units).map(([key, value]) => {
    const text =
      value.unit_text?.eng ?? Object.values(value.unit_text ?? {})[0];
    const label = unitOptionLabelOf(text, value.unit_id);
    return {
      name: label,
      searchText: [
        value.unit_id,
        value.external_reference,
        text?.name,
        text?.symbol,
        text?.description,
      ]
        .filter(Boolean)
        .join(" "),
      source_id: key,
    };
  });
  const selectedUnitEntry = unit
    ? Object.entries(units).find(
        ([key, candidate]) =>
          key === unit.unit_id || candidate.unit_id === unit.unit_id,
      )
    : undefined;
  const selectedUnitOption = unit
    ? {
        name: unitOptionLabelOf(unit.unit_text?.eng, unit.unit_id),
        source_id: selectedUnitEntry?.[0] ?? unit.unit_id,
      }
    : null;
  const findUnitByReference = (reference: number | string | null) =>
    reference == null
      ? undefined
      : Object.values(units).find(
          (u) => u.external_reference === String(reference),
        );

  // Picking a unit and clearing the field both stop the auto-pick; only
  // picking a node starts it again. Auto-pick writes below do NOT change the
  // flag — writing isn't picking.
  const handleUnitSelection = (selected: MachineNameAndSourceId | null) => {
    setUnit(
      selected
        ? units[selected.source_id] ??
            Object.values(units).find(
              (candidate) => candidate.unit_id === selected.source_id,
            ) ??
            null
        : null,
    );
    setShouldAutoPickUnit(false);
  };

  // Reveal the form once the machines list, the unit catalog and the initial
  // node load are done — otherwise the node would open on the raw id the seed
  // carries and swap to its name a moment later.
  useEffect(() => {
    if (!isInitializing) return;
    if (
      !isLoadingMachines &&
      !isLoadingUnits &&
      !(machine !== null && isLoadingNodes)
    ) {
      setIsInitializing(false);
    }
  }, [isLoadingMachines, isLoadingNodes, unitsStatus, machine, isInitializing]);

  // Load machines once.
  useEffect(() => {
    setIsLoadingMachines(true);
    getAllSourcesListUseCase().then((result) => {
      if (result.success && result.data) {
        setMachineOptions(
          Object.values(result.data).map((s) => ({
            name: s.name,
            source_id: s.source_id,
          })),
        );
      } else {
        setMachineOptions([]);
        console.error("Failed to load machines:", result.error);
      }
      setIsLoadingMachines(false);
    });
  }, []);

  // Units come from the global slice (loaded at dashboards-load). Lazy
  // fallback: if the slice was never attempted, fetch once so the dropdown
  // isn't empty.
  const unitsAttempted = useRef(false);
  useEffect(() => {
    if (unitsStatus !== "idle" || unitsAttempted.current) return;
    unitsAttempted.current = true;
    dispatch(unitsRequested());
    getUnitsUseCase().then((result) => {
      if (result.success && result.data) {
        dispatch(unitsLoaded(result.data));
      } else {
        dispatch(unitsFailed(result.error ?? "Failed to fetch units"));
      }
    });
  }, [unitsStatus, dispatch]);

  // The sources list loads asynchronously, so the prefill hands the machine id
  // forward and this effect swaps in the option's name once it arrives.
  useEffect(() => {
    if (machineOptions.length === 0) return;
    const machineId = savedDataSource?.group ?? widget.query?.source;
    if (!machineId) return;
    const match = machineOptions.find((m) => m.source_id === machineId);
    if (!match) return;
    setMachine((prev) => {
      if (!prev) return prev;
      // If the user is currently looking at a DIFFERENT machine, do NOT
      // overwrite — the source_id test stops that race.
      if (prev.source_id !== match.source_id) return prev;
      if (prev.name === match.name) return prev;
      return { name: match.name, source_id: match.source_id };
    });
  }, [machineOptions, savedDataSource, widget.query?.source]);

  // Load nodes when the machine is selected. single-text-value accepts string
  // nodes as well as numeric ones (status-style nodes are the point of it), so
  // this editor takes the wider option list.
  useEffect(() => {
    if (!machine) {
      setNodeOptions([]);
      setNodeUnits({});
      setNode(null);
      return;
    }
    setIsLoadingNodes(true);
    let cancelled = false;
    getActiveTimeseriesUseCase(machine.source_id).then((result) => {
      if (cancelled) return;
      if (result.success && result.data) {
        setNodeOptions(toNumericAndStringNodeOptions(result.data.timeseries));
        setNodeUnits(
          Object.fromEntries(
            Object.values(result.data.timeseries)
              .filter((timeseries) => timeseries.data_type === "numeric")
              .map((timeseries) => [
                timeseries.source,
                getNumericNodeUnit(result.data.timeseries, timeseries.source),
              ]),
          ),
        );
        // The stored node belongs to the stored machine: restore it when that
        // machine's options load, leave the node empty for any other machine.
        // Only while the user has not picked a machine by hand — recalling the
        // same machine must not resurrect the saved node.
        if (
          !machineTouchedRef.current &&
          savedDataSource &&
          savedDataSource.group === machine.source_id &&
          savedDataSource.source
        ) {
          setNode({
            name: savedDataSource.source,
            source_id: savedDataSource.source,
          });
        }
      } else {
        setNodeOptions([]);
        setNodeUnits({});
        console.error("Failed to load nodes:", result.error);
      }
      setIsLoadingNodes(false);
    });

    return () => {
      cancelled = true;
    };
  }, [machine, savedDataSource]);

  // Same id → name swap for the node, against the same list it displays.
  useEffect(() => {
    if (nodeOptions.length === 0 || !savedDataSource) return;
    const match = nodeOptions.find(
      (n) => n.source_id === savedDataSource.source,
    );
    if (!match) return;
    setNode((prev) => {
      if (!prev) return prev;
      if (prev.source_id !== match.source_id) return prev;
      if (prev.name === match.name) return prev;
      return { name: match.name, source_id: match.source_id };
    });
  }, [nodeOptions, savedDataSource]);

  // Restore the saved unit once the catalog is available (the global load may
  // still be in flight when the editor opens).
  useEffect(() => {
    if (isPendingWidget) return;
    if (savedUnitId == null) return;
    const match = getUnitById(units, savedUnitId);
    if (match) setUnit(match);
  }, [units, savedUnitId, isPendingWidget]);

  // The picked node drives the unit: a node with a unit fills the field, a
  // node without one clears it so the user can type one. Picking or clearing
  // a unit (handleUnitSelection) suspends this until the node changes again.
  useEffect(() => {
    if (!shouldAutoPickUnit) return;
    const nodeUnit = node ? nodeUnits[node.source_id] : null;
    const match = nodeUnit != null ? findUnitByReference(nodeUnit) : undefined;
    setUnit(match ?? null);
  }, [node, nodeUnits, units, shouldAutoPickUnit]);

  // Mirror the unit auto-pick on the label: while the user hasn't typed,
  // the label follows the bound node's name on every node change. The
  // first keystroke flips the flag and this stops writing.
  useEffect(() => {
    if (!shouldAutoFillLabel) return;
    if (!node) return;
    setLabel(node.name ?? "");
  }, [node, shouldAutoFillLabel]);

  const validate = (): string | null => {
    if (!machine) return texts.machineRequired;
    if (!node) return texts.nodeRequired;
    if (label.trim() === "") return texts.labelRequired;
    if (lookbackMs == null) return texts.lookbackRequired;
    if (valueScale.trim() !== "" && parseUserNumber(valueScale) === null) {
      return texts.scaleInvalid;
    }
    return null;
  };

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    const updated: Widget = {
      ...widget,
      // single-text-value names itself through its label, so its title is
      // whatever the template gave it.
      title: widget.title,
      image_url: widget.image_url ?? null,
      query: {
        ...buildWidgetQuery(
          machine?.source_id ?? "",
          node?.source_id ?? "",
          pollingInterval ?? undefined,
        ),
        // Duration as ISO so a re-open rehydrates the same value, and the
        // scalar fetch uses the window for the rendered timestamp.
        window: {
          duration: durationToIso(lookbackMs ?? DURATION_GAUGE_WIDGET_MS),
        },
      },
      // No threshold, no target, no bounds — the widget just renders a label +
      // a value + a unit + a timestamp. Pass the saved semantics through as-is
      // so BE-stored extras (currently none, but the type allows them)
      // round-trip cleanly.
      semantics: { ...(widget.semantics as Record<string, unknown>) },
      encoding: {
        ...widget.encoding,
        ...scaleTransformsKey(
          widget.encoding?.transforms,
          ["value"],
          buildScaleTransforms([{ field: "value", scale: valueScale }]),
        ),
      },
      presentation: (() => {
        // Box unticked → drop both per-widget overrides: the widget renders
        // the dashboard-theme custom properties `--color-cp-widget-primary` /
        // `--color-cp-widget-secondary` instead. Box ticked → persist both
        // picks, seeded with `DEFAULT_*` when freshly enabled so the active
        // swatch a user sees is what's saved.
        //
        // Any pre-existing `color` / `label_color` is STRIPPED before the
        // spread — a plain `...widget.presentation` would re-emit the prior
        // stored colours on the unticked-→-save path even after the user
        // flipped the checkbox off.
        const withoutColors = Object.fromEntries(
          Object.entries(widget.presentation ?? {}).filter(
            ([k]) => k !== "color" && k !== "label_color",
          ),
        );
        return {
          ...withoutColors,
          unit: unit?.unit_id ?? null,
          // `validate` is the authority on required fields, so `label` always
          // lands trimmed-non-empty here.
          label: label.trim(),
          ...(isUsingCustomColors && {
            label_color: labelColor ?? DEFAULT_LABEL_COLOR,
            color: color ?? DEFAULT_TREND_LINE_COLOR,
          }),
        };
      })(),
    };
    save(updated, { validate });
  };

  if (isInitializing) {
    return (
      <div className={styles[`${cssClassName}__loading`]}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles[`${cssClassName}__outer-wrapper`]}>
      <MachineNodeUnitFields
        texts={{
          machineLabel: texts.machineLabel,
          noMachines: texts.noMachines,
          machinesPlaceholder: texts.machinesPlaceholder,
          nodeLabel: texts.nodeLabel,
          noNodes: texts.noNodes,
          nodesPlaceholder: texts.nodesPlaceholder,
          unitLabel: texts.unitLabel,
          noUnits: texts.noUnits,
          unitsPlaceholder: texts.unitsPlaceholder,
        }}
        machineOptions={machineOptions}
        selectedMachine={machine}
        onSelectMachine={(selected) => {
          machineTouchedRef.current = true;
          setMachine(selected);
          setNode(null);
          // The unit belongs to the node, so it goes when the node goes.
          setUnit(null);
        }}
        isLoadingMachines={isLoadingMachines}
        nodeOptions={nodeOptions}
        selectedNode={node}
        onSelectNode={(selected) => {
          setNode(selected);
          setShouldAutoPickUnit(true);
        }}
        isLoadingNodes={isLoadingNodes}
        unitOptions={unitOptions}
        selectedUnit={selectedUnitOption}
        onSelectUnit={handleUnitSelection}
        isLoadingUnits={isLoadingUnits}
      />

      <div className={styles[`${cssClassName}__field`]}>
        <ScaleInput value={valueScale} onChange={setValueScale} />
      </div>

      <div className={styles[`${cssClassName}__field`]}>
        <Input2025
          inputPlaceHolder={texts.labelPlaceholder}
          labelText={texts.labelLabel}
          inputValue={label}
          onChangeFunc={(e) => {
            setShouldAutoFillLabel(false);
            setLabel(e.target.value);
          }}
          inputVariant="dark-background"
        />
      </div>

      <LookbackAndPollingFields
        i18n={i18n}
        lookbackMs={lookbackMs}
        setLookbackMs={setLookbackMs}
        pollingInterval={pollingInterval}
        setPollingInterval={setPollingInterval}
      />

      <CheckBoxWithText
        text={texts.customColorsCheckbox}
        checkboxState={isUsingCustomColors}
        handleCheckBoxClick={() => {
          const next = !isUsingCustomColors;
          setIsUsingCustomColors(next);
          // Flipping ON from OFF: seed both pickers with palette defaults so a
          // swatch shows as active immediately. Flipping OFF: wipe locally so
          // the next save drops both keys from `presentation` and the widget
          // falls back to the dashboard-theme custom properties.
          if (next) {
            setColor((c) => c ?? DEFAULT_TREND_LINE_COLOR);
            setLabelColor((c) => c ?? DEFAULT_LABEL_COLOR);
          } else {
            setColor(null);
            setLabelColor(null);
          }
        }}
      />

      {isUsingCustomColors && (
        <>
          <div className={styles[`${cssClassName}__field`]}>
            <ColorSwatchPicker
              label={texts.labelColorLabel}
              value={labelColor}
              swatches={TREND_LINE_PALETTE}
              onChange={setLabelColor}
            />
          </div>
          <div className={styles[`${cssClassName}__field`]}>
            <ColorSwatchPicker
              label={texts.valueColorLabel}
              value={color}
              swatches={TREND_LINE_PALETTE}
              onChange={setColor}
            />
          </div>
        </>
      )}

      <Spacer16 />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={texts.saveButton}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={texts.cancelButton}
          variant="secondary"
          // Cancel must NOT fire mid-save: it would dispatch
          // removePendingWidget while POST/PUT are still in flight and leak a
          // server-side orphan.
          disabled={isSaving}
        />
      </FlexRowContainer>
    </div>
  );
};
